import { useCallback, useMemo, useState } from 'react';
import { useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import DecisionFooter from '../../DecisionFooter.jsx';
import { useEventDraft } from '../../../context/useEventDraft.js';
import { useWorkQueueContext } from '../../../context/useWorkQueueContext.js';
import { tallyClaimDecisions } from '../shared/claims/index.js';
import { ShieldCheck } from 'lucide-react';

const EMPTY_CLAIMS = [];

/**
 * Gov Review DECISION bar: Submit review, gated on every item having a
 * decision. Confirm swaps for an inline summary (Cancel / Confirm Review).
 */
function GovReviewDecision({ event, actions }) {
  const { t } = useTranslation('work-queues-app');
  const toast = useToast();
  const { completeGovReview } = useWorkQueueContext();
  const { itemDecisions, itemCorrections } = useEventDraft();
  const [confirming, setConfirming] = useState(false);

  const claims = event.detail?.claims ?? EMPTY_CLAIMS;
  const requester = event.detail?.requestedBy?.name ?? '';

  const { canSubmit, counts } = useMemo(
    () => ({
      canSubmit: claims.length > 0 && claims.every((claim) => itemDecisions[claim.id]),
      counts: tallyClaimDecisions(claims, itemDecisions, { approveKey: 'approved' }),
    }),
    [claims, itemDecisions],
  );

  const handleConfirm = useCallback(() => {
    completeGovReview(event.id, {
      ...counts,
      corrections: itemCorrections,
      requester,
    });
    toast.success({
      title: t('govReview.submittedTitle'),
      message: t('govReview.submittedMessage'),
    });
    setConfirming(false);
  }, [completeGovReview, counts, event.id, itemCorrections, requester, t, toast]);

  const primaryActions = useMemo(() => {
    if (confirming) {
      return [
        {
          id: 'cancel',
          label: t('actions.cancel'),
          variant: 'secondary',
          onClick: () => setConfirming(false),
        },
        {
          id: 'confirm',
          label: t('govReview.confirmReview'),
          onClick: handleConfirm,
        },
      ];
    }
    return [
      {
        id: 'submit',
        label: t('govReview.submitReview'),
        icon: ShieldCheck,
        size: 'md',
        disabled: !canSubmit,
        onClick: () => setConfirming(true),
      },
    ];
  }, [confirming, t, handleConfirm, canSubmit]);

  // Pluralize on `approved` — that is the grammatical subject of confirmSummary.
  // Fall back to total decided when nothing was approved so locales that treat
  // `0` as singular still pick a sensible form for mixed reject/correct reviews.
  const summaryCount =
    counts.approved > 0
      ? counts.approved
      : counts.approved + counts.rejected + counts.corrected + counts.flagged;
  const summary = confirming
    ? t('govReview.confirmSummary', {
        ...counts,
        count: summaryCount,
        requester,
      })
    : undefined;
  const hint = (!canSubmit && t('govReview.submitHint')) || undefined;

  return (
    <DecisionFooter
      event={event}
      primaryActions={primaryActions}
      hint={hint}
      summary={summary}
      onSkip={actions.skip}
      onAsk={actions.ask}
      onEscalate={actions.escalate}
      onTrace={actions.trace}
      extraMenuItems={actions.extraMenuItems}
      sticky={actions.sticky}
    />
  );
}

export default GovReviewDecision;
